import {ChartConfig} from "../../../../../components/general/chart.tsx";
import {Cell, Pie, PieChart, Sector} from "recharts";
import {useEffect, useState} from "react";
import {MonthOverviewDataSpec} from "./MonthOverview.tsx";
import {FilterOperation} from "../TransactionsPage/FilterService.ts";
import {getAccentChartColor, PIE_DEFAULT_FILL} from "../../../../../lib/accentPalette";

const chartConfig = {} satisfies ChartConfig;

type MonthOverviewPieChartProps = {
    data: MonthOverviewDataSpec,
    flow: 'outflow' | 'inflow',
    currentMonth: Date,
    className?: string;
    hoveredCategory?: string | null;
};

function renderActiveSlice(props: any) {
    const {cx, cy, innerRadius, outerRadius, startAngle, endAngle, midAngle, fill, payload} = props;
    const labelRadius = outerRadius + 40;
    const labelX = cx + labelRadius * Math.cos(-midAngle * Math.PI / 180);
    const labelY = cy + labelRadius * Math.sin(-midAngle * Math.PI / 180);

    return (
        <g>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius + 6}
                outerRadius={outerRadius + 6}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                style={{strokeWidth: 1}}
            />
            <text x={labelX} y={labelY} fill="gray" fontSize="13" textAnchor="middle">
                {payload.percentage.toFixed(1)}%
            </text>
        </g>
    );
}

export default function MonthOverviewPieChart({data, flow, currentMonth, className, hoveredCategory}: MonthOverviewPieChartProps) {
    const [chartData, setChartData] = useState<{
        name: string;
        percentage: number;
        amount: number;
        color: string
    }[]>([]);
    const [config, setConfig] = useState<ChartConfig>({});

    useEffect(() => {
        if (!data) return;

        const allocations = selectForFlow(flow, data.outflow_allocations, data.inflow_allocations);
        const total = selectForFlow(flow, data.current_month_outflow, data.current_month_inflow);

        const nextChartData = allocations?.map((allocation) => ({
            name: allocation.name,
            percentage: total ? (allocation.total / total) * 100 : 0,
            amount: allocation.total / 100,
            color: getAccentChartColor(allocation.icon),
        })) ?? [];

        setChartData(nextChartData);

        const newConfig: ChartConfig = {};
        nextChartData.forEach((category) => {
            newConfig[category.name] = {
                label: category.name,
                color: category.color,
            };
        });
        setConfig(newConfig);
    }, [data, flow]);

    if (!data) {
        return <div className="p-8 rounded-lg bg-white border-2 border-gray-50 w-full text-center">Geen gegevens beschikbaar</div>;
    }

    const activeIndex = hoveredCategory
        ? chartData.findIndex((entry) => entry.name === hoveredCategory)
        : -1;

    return (
        <div className={`w-full max-w-md mx-auto ${className}`}>
            <PieChart width={400} height={310}>
                <Pie
                    data={chartData}
                    cx={200}
                    cy={150}
                    innerRadius={60}
                    outerRadius={100}
                    fill={PIE_DEFAULT_FILL}
                    paddingAngle={6}
                    dataKey="percentage"
                    activeIndex={activeIndex >= 0 ? activeIndex : undefined}
                    activeShape={renderActiveSlice}
                    isAnimationActive={false}
                    style={{
                        strokeWidth: 1
                    }}
                    onClick={(e: any, index: number) => {
                        const category = chartData[index].name;
                        const date = new Date(currentMonth);
                        const startDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
                        const endDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()}`;

                        const filters = [
                            {
                                id: 'filterFlow',
                                operation: FilterOperation.EQUALS,
                                columnId: 'flow',
                                values: [flow]
                            },
                            {
                                id: 'filterCategory',
                                operation: FilterOperation.EQUALS,
                                columnId: 'category',
                                values: [category]
                            },
                            {
                                id: 'filterBookingDate',
                                operation: FilterOperation.BETWEEN,
                                columnId: 'bookingDate',
                                values: [startDate, endDate]
                            }
                        ];

                        window.location.href = `/transactions?filters=${encodeURIComponent(JSON.stringify(filters))}`;
                    }}
                    label={({midAngle, innerRadius, outerRadius, cx, cy, index}) => {
                        const radius = outerRadius + 40;
                        const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                        const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

                        return (
                            <g>
                                <text
                                    x={x}
                                    y={y}
                                    fill="gray"
                                    fontSize="13"
                                    textAnchor="middle"
                                >
                                    {chartData[index].percentage.toFixed(1)}%
                                </text>
                            </g>
                        );
                    }}
                    labelLine={true}
                >
                    {chartData.map((entry, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                        />
                    ))}
                </Pie>
            </PieChart>
        </div>
    );
}

function selectForFlow(flow: string, outflow: any, inflow: any) {
    if (flow === 'outflow')
        return outflow;
    return inflow;
}
